import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FirebaseContext } from '../Firebase';
import { AuthUserContext } from '../Session';
import * as ROUTES from '../../constants/routes';
import './Blog.css';

const BlogPage = () => {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState(null);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const currentLang = i18n.language || 'en';

  useEffect(() => {
    loadArticles();
  }, [selectedTag, currentLang]);

  const loadArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = selectedTag 
        ? await firebase.doGetArticlesByTag(selectedTag)
        : await firebase.doGetPublishedArticles();
      console.log('Loaded articles:', data); // Debug log
      
      // Filter articles that have translation for current language
      const translatedArticles = data.filter(article => 
        article.translations && article.translations[currentLang]
      );
      
      setArticles(translatedArticles);
    } catch (error) {
      console.error('Error loading articles:', error);
      setError(error.message);
    }
    setLoading(false);
  };

  // Get all unique tags from articles (in current language only)
  const allTags = [...new Set(
    articles.flatMap(a => 
      a.translations?.[currentLang]?.tags || []
    )
  )];

  // Get translated content for an article
  const getTranslation = (article) => {
    return article.translations?.[currentLang] || article.translations?.en || {};
  };

//   Access control: all users can view the blog even if not signed in
  if (!authUser && !!authUser) {
    return <div className="loading">{t('blog.please_sign_in', 'Please sign in to view the blog.')}</div>;
  }

  return (
    <div className="blog-container">
      <header className="blog-header">
        <h1>{t('blog.title', 'Construction Planning Blog')}</h1>
        <p>{t('blog.subtitle', 'Insights, tips, and best practices for construction management')}</p>
      </header>

      {/* Sidebar toggle button */}
      {allTags.length > 0 && (
        <button 
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={t('blog.toggle_filters','Toggle filters')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="6" x2="20" y2="6"/>
            <line x1="4" y1="12" x2="20" y2="12"/>
            <line x1="4" y1="18" x2="20" y2="18"/>
          </svg>
          <span>{t('blog.filters','Filters')} {selectedTag && `(${selectedTag})`}</span>
        </button>
      )}

      {/* Retractable sidebar for tags */}
      {allTags.length > 0 && (
        <>
          <div 
            className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          />
          <div className={`tag-sidebar ${sidebarOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
              <h3>{t('blog.filter_by_tags','Filter by Tags')}</h3>
              <button 
                className="sidebar-close"
                onClick={() => setSidebarOpen(false)}
                aria-label={t('blog.close_filters','Close filters')}
              >
                ×
              </button>
            </div>
            <div className="tag-filter-sidebar">
              <button 
                className={!selectedTag ? 'active' : ''} 
                onClick={() => {
                  setSelectedTag(null);
                  setSidebarOpen(false);
                }}
              >
                {t('blog.all_articles','All Articles')}
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  className={"tag" + (selectedTag === tag ? ' active' : '')}
                  onClick={() => {
                    setSelectedTag(tag);
                    setSidebarOpen(false);
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="loading">{t('blog.loading_articles','Loading articles...')}</div>
      ) : error ? (
        <div className="no-articles">
          <p>{t('blog.error_loading', { error })}</p>
          <p>{t('blog.security_note', 'This might be a Firestore security rules issue. Check the console for details.')}</p>
        </div> 
      ) : articles.length === 0 ? (
        <div className="no-articles">
          <p>{t('blog.no_articles','No articles published yet. Check back soon!')}</p>
        </div>
      ) : (
        <div className="articles-grid">
          {articles.map(article => {
            const translation = getTranslation(article);
            return (
              <article key={article.slug} className="article-card">
                {article.featuredImage && (
                  <img src={article.featuredImage} alt={translation.title} className="card-image" />
                )}
                <div className="article-content">
                  <h5>
                    <Link to={`${ROUTES.BLOG}/${article.slug}`}>
                      {translation.title}
                    </Link>
                  </h5>
                  <div className="article-meta" style={{display: 'none'}}>
                    <span>{article.publishedAt?.toDate ? new Date(article.publishedAt.toDate()).toLocaleDateString() : t('blog.recently','Recently')}</span>
                    {article.author && <span> • {article.author}</span>}
                  </div>
                  <p className="article-excerpt" style={{display: 'none'}}>{translation.excerpt}</p>
                  {/* Tags - Hidden for now */}
                  {translation.tags && translation.tags.length > 0 && (
                    <div className="article-tags">
                      {translation.tags.map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  <Link to={`${ROUTES.BLOG}/${article.slug}`} className="read-more">
                    {t('blog.read_more', 'Read More')} →
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BlogPage;
